import { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts";

import type { PerformanceFlightRow } from "@/api/performance";
import type { WidgetComponentProps } from "../types";
import { usePerformanceData, usePerformanceLoading } from "./PerformanceDataContext";

/**
 * All Performance widgets share this group name so hover + zoom sync across
 * every chart on the page — ECharts does the wiring via `echarts.connect()`.
 */
const PERF_CHART_GROUP = "performance-dashboard";

/**
 * Direct ECharts integration — we previously used `echarts-for-react`, but its
 * two-step init pattern (init a temp chart, wait for the `"finished"` event to
 * measure the container, dispose, then recreate with that width/height baked
 * in) races with react-grid-layout's async width measurement. Inside the
 * dashboard the grid-item width wasn't known at the moment the wrapper latched
 * it, so the chart was pinned to a stale or zero width and the series drew
 * squashed against the left edge until a window resize kicked it.
 *
 * Driving `echarts.init` ourselves and letting a `ResizeObserver` call
 * `chart.resize()` whenever the container size changes gives us a correct
 * render on first paint plus automatic reflow when the widget is resized via
 * the grid.
 */
function PerfChart({ option }: { option: Record<string, unknown> }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const chart = echarts.init(node, undefined, { renderer: "canvas" });
    chart.group = PERF_CHART_GROUP;
    chartRef.current = chart;
    const observer = new ResizeObserver(() => chart.resize());
    observer.observe(node);
    const onWindowResize = () => chart.resize();
    window.addEventListener("resize", onWindowResize);
    echarts.connect(PERF_CHART_GROUP);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", onWindowResize);
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    chart.setOption(option, { notMerge: true });
    // Give the grid-item one frame to settle its final width, then re-measure.
    const raf = requestAnimationFrame(() => chart.resize());
    return () => cancelAnimationFrame(raf);
  }, [option]);

  return <div ref={containerRef} style={{ height: "100%", width: "100%" }} />;
}

function LoadingState() {
  return (
    <div className="widget-empty perf-widget__loading" role="status">
      <span className="perf-widget__spinner" aria-hidden="true" />
      <span>Loading…</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Shared chart theming                                               */
/* ------------------------------------------------------------------ */

const PRIMARY = "#FF6B1A";
const ACCENT = "#FBBF24";
const COOL = "#60A5FA";
const GOOD = "#4ADE80";
const DANGER = "#EF4444";

function readThemeColors() {
  if (typeof document === "undefined") {
    return {
      text: "#E8ECF1",
      textMuted: "#A0AAB8",
      border: "rgba(42,52,68,0.5)",
      gridLine: "rgba(42,52,68,0.5)",
      tooltipBg: "rgba(10,14,20,0.96)",
    };
  }
  const body = getComputedStyle(document.body);
  return {
    text: body.getPropertyValue("--text").trim() || "#E8ECF1",
    textMuted: body.getPropertyValue("--text-muted").trim() || "#A0AAB8",
    border: body.getPropertyValue("--border").trim() || "rgba(42,52,68,0.5)",
    gridLine: body.getPropertyValue("--border").trim() || "rgba(42,52,68,0.3)",
    tooltipBg:
      document.documentElement.dataset.uiTheme === "light"
        ? "rgba(255,253,249,0.96)"
        : "rgba(10,14,20,0.96)",
  };
}

function baseAxisStyle() {
  const c = readThemeColors();
  return {
    axisLine: { lineStyle: { color: c.border } },
    axisTick: { show: false },
    axisLabel: { color: c.textMuted, fontSize: 10 },
    splitLine: { lineStyle: { color: c.gridLine, type: "dashed" as const } },
  };
}

function baseTooltip() {
  const c = readThemeColors();
  return {
    trigger: "axis" as const,
    backgroundColor: c.tooltipBg,
    borderColor: c.border,
    textStyle: { color: c.text, fontSize: 12 },
  };
}

function baseGrid(size: "xs" | "s" | "m" | "l") {
  if (size === "xs" || size === "s") return { top: 8, right: 10, bottom: 8, left: 10, containLabel: true };
  return { top: 28, right: 20, bottom: 54, left: 50, containLabel: false };
}

/**
 * Table-like widgets still want a "show last N" preview at small sizes. Chart
 * widgets use the full filtered `flight_rows` so the time axis reflects the
 * full selected range.
 */
function sliceForSize(rows: PerformanceFlightRow[], size: "xs" | "s" | "m" | "l"): PerformanceFlightRow[] {
  if (size === "xs") return rows.slice(-5);
  if (size === "s") return rows.slice(-10);
  if (size === "m") return rows.slice(-20);
  return rows;
}

/**
 * Normalize a row's metric into a plain `number | null` so chart series can
 * consume it without defensive checks.
 */
function valuesOf(
  rows: PerformanceFlightRow[],
  pick: (row: PerformanceFlightRow) => number | null | undefined,
): (number | null)[] {
  return rows.map((row) => {
    const raw = pick(row);
    return typeof raw === "number" && Number.isFinite(raw) ? raw : null;
  });
}

function axisLabelConfig(size: "xs" | "s" | "m" | "l") {
  const base = baseAxisStyle().axisLabel;
  if (size === "xs") return { ...base, show: false };
  if (size === "s") return { ...base, fontSize: 9, hideOverlap: true };
  return { ...base, fontSize: 10, hideOverlap: true, margin: 10 };
}

/**
 * Category x-axis keyed by flight order (1..N). The actual time of each flight
 * lives in the tooltip — the axis itself is just "Flight N".
 */
function makeIndexXAxis(
  size: "xs" | "s" | "m" | "l",
  flightCount: number,
) {
  const base = baseAxisStyle();
  const labels = Array.from({ length: flightCount }, (_, i) => String(i + 1));
  return {
    type: "category" as const,
    data: labels,
    boundaryGap: false,
    ...base,
    axisLabel: axisLabelConfig(size),
  };
}

function escapeHtml(value: string | null | undefined): string {
  if (value == null) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Axis tooltip that renders flight metadata (session, pilot, date) as a rich
 * header above the series values for the hovered flight index. `format` lets
 * each widget customize how numbers render (units, abs(), etc).
 */
function indexTooltip(
  rows: PerformanceFlightRow[],
  format?: (value: number | null | undefined, seriesName?: string) => string,
) {
  const c = readThemeColors();
  const base = baseTooltip();
  return {
    ...base,
    axisPointer: {
      type: "line" as const,
      lineStyle: {
        color: PRIMARY,
        width: 1,
        type: "dashed" as const,
        opacity: 0.85,
      },
      label: { show: false },
    },
    formatter: (params: unknown) => {
      const arr = Array.isArray(params) ? params : [params];
      if (!arr.length) return "";
      const first = arr[0] as { dataIndex?: number };
      const idx = typeof first.dataIndex === "number" ? first.dataIndex : 0;
      const row = rows[idx];
      if (!row) return "";
      const metaParts: string[] = [];
      if (row.owner_label) metaParts.push(escapeHtml(row.owner_label));
      if (row.date_display) metaParts.push(escapeHtml(row.date_display));
      const header = `<div style="font-weight:600;font-size:12px">Flight ${idx + 1} — ${escapeHtml(row.session_name)}</div>`;
      const meta = metaParts.length
        ? `<div style="color:${c.textMuted};font-size:11px;margin-top:2px">${metaParts.join(" · ")}</div>`
        : "";
      const lines = arr
        .map((raw) => {
          const p = raw as { value?: number | null; seriesName?: string; marker?: string };
          const formatted = format
            ? format(p.value, p.seriesName)
            : p.value === null || p.value === undefined
              ? "—"
              : String(p.value);
          return `<div style="display:flex;gap:14px;justify-content:space-between;align-items:center;margin-top:3px"><span>${p.marker ?? ""}${escapeHtml(p.seriesName ?? "")}</span><strong style="font-variant-numeric:tabular-nums">${escapeHtml(formatted)}</strong></div>`;
        })
        .join("");
      return `<div style="min-width:200px">${header}${meta}<div style="margin-top:6px">${lines}</div></div>`;
    },
  };
}

function useFlightRows() {
  const data = usePerformanceData();
  return data?.flight_rows ?? [];
}

function WidgetShell({
  subtitle,
  children,
}: {
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="perf-widget">
      {subtitle && <div className="perf-widget__subtitle">{subtitle}</div>}
      <div className="perf-widget__body">{children}</div>
    </div>
  );
}

function EmptyState({ reason }: { reason?: string }) {
  return <p className="widget-empty">{reason ?? "No data for current filters."}</p>;
}

/**
 * Picks between the loading and empty placeholders based on the shared query
 * state — while the first Performance fetch is in flight we show a spinner
 * instead of "No data", so the dashboard never misleads the user.
 */
function RowsPlaceholder() {
  const isLoading = usePerformanceLoading();
  return isLoading ? <LoadingState /> : <EmptyState />;
}

/* ------------------------------------------------------------------ */
/*  Summary KPIs                                                       */
/* ------------------------------------------------------------------ */

function mean(values: (number | null | undefined)[]): number | null {
  const clean = values.filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  if (!clean.length) return null;
  return clean.reduce((a, b) => a + b, 0) / clean.length;
}

export function FlightsInWindowWidget({ sizeVariant }: WidgetComponentProps) {
  const data = usePerformanceData();
  const count = data?.summary.flight_count ?? 0;
  const total = data?.summary.total_flight_count ?? 0;
  return (
    <div className={`perf-kpi perf-kpi--${sizeVariant}`}>
      <span className="perf-kpi__value">{count}</span>
      <span className="perf-kpi__sub">of {total} total</span>
    </div>
  );
}

export function AvgSmoothnessWidget({ sizeVariant }: WidgetComponentProps) {
  const rows = useFlightRows();
  const avg = mean(rows.map((r) => r.smoothness_score));
  return (
    <div className={`perf-kpi perf-kpi--${sizeVariant}`}>
      <span className="perf-kpi__value" style={{ color: PRIMARY }}>
        {avg !== null ? avg.toFixed(0) : "—"}
      </span>
      <span className="perf-kpi__sub">/ 100</span>
    </div>
  );
}

export function AvgSpeedWidget({ sizeVariant }: WidgetComponentProps) {
  const rows = useFlightRows();
  const avg = mean(rows.map((r) => r.avg_speed_kmh));
  return (
    <div className={`perf-kpi perf-kpi--${sizeVariant}`}>
      <span className="perf-kpi__value">{avg !== null ? avg.toFixed(1) : "—"}</span>
      <span className="perf-kpi__sub">km/h</span>
    </div>
  );
}

export function PeakGKpiWidget({ sizeVariant }: WidgetComponentProps) {
  const rows = useFlightRows();
  const peak = rows.reduce<number | null>((acc, r) => {
    if (r.peak_g === null || r.peak_g === undefined) return acc;
    return acc === null ? r.peak_g : Math.max(acc, r.peak_g);
  }, null);
  return (
    <div className={`perf-kpi perf-kpi--${sizeVariant}`}>
      <span className="perf-kpi__value" style={{ color: ACCENT }}>
        {peak !== null ? peak.toFixed(2) : "—"}
      </span>
      <span className="perf-kpi__sub">g peak</span>
    </div>
  );
}

export function TotalDistanceKpiWidget({ sizeVariant }: WidgetComponentProps) {
  const rows = useFlightRows();
  const total = rows.reduce((acc, r) => acc + (r.distance_km ?? 0), 0);
  return (
    <div className={`perf-kpi perf-kpi--${sizeVariant}`}>
      <span className="perf-kpi__value">{total.toFixed(1)}</span>
      <span className="perf-kpi__sub">km total</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Trend charts                                                       */
/* ------------------------------------------------------------------ */

export function SmoothnessTrendWidget({ sizeVariant }: WidgetComponentProps) {
  const rows = useFlightRows();
  const option = useMemo(() => {
    const data = valuesOf(rows, (r) => r.smoothness_score);
    return {
      grid: baseGrid(sizeVariant),
      xAxis: makeIndexXAxis(sizeVariant, rows.length),
      yAxis: {
        type: "value",
        min: 0,
        max: 100,
        ...baseAxisStyle(),
      },
      tooltip: indexTooltip(rows, (v) => (v === null || v === undefined ? "—" : v.toFixed(0))),
      series: [
        {
          name: "Smoothness",
          type: "line",
          smooth: 0.25,
          showSymbol: sizeVariant !== "xs",
          symbol: "circle",
          symbolSize: 6,
          data,
          lineStyle: { width: 2, color: PRIMARY },
          itemStyle: { color: PRIMARY },
          areaStyle: { color: "rgba(255, 107, 26, 0.12)" },
        },
      ],
    };
  }, [rows, sizeVariant]);
  if (!rows.length) return <RowsPlaceholder />;
  return (
    <WidgetShell subtitle={sizeVariant === "xs" || sizeVariant === "s" ? undefined : "Smoothness score (0–100) per flight"}>
      <PerfChart option={option} />
    </WidgetShell>
  );
}

export function SpeedBreakdownWidget({ sizeVariant }: WidgetComponentProps) {
  const rows = useFlightRows();
  const option = useMemo(() => {
    const avg = valuesOf(rows, (r) => r.avg_speed_kmh);
    const min = valuesOf(rows, (r) => r.min_speed_kmh);
    const max = valuesOf(rows, (r) => r.max_speed_kmh);
    return {
      grid: baseGrid(sizeVariant),
      legend: {
        show: sizeVariant !== "xs" && sizeVariant !== "s",
        textStyle: { color: readThemeColors().textMuted, fontSize: 11 },
        right: 12,
        top: 4,
      },
      xAxis: makeIndexXAxis(sizeVariant, rows.length),
      yAxis: { type: "value", ...baseAxisStyle() },
      tooltip: indexTooltip(rows, (v) => (v === null || v === undefined ? "—" : `${v.toFixed(1)} km/h`)),
      series: [
        {
          name: "Avg",
          type: "line",
          smooth: 0.25,
          data: avg,
          showSymbol: false,
          lineStyle: { width: 2.5, color: PRIMARY },
          itemStyle: { color: PRIMARY },
        },
        {
          name: "Min",
          type: "line",
          smooth: 0.25,
          data: min,
          showSymbol: false,
          lineStyle: { width: 1.5, color: COOL, type: "dashed" },
          itemStyle: { color: COOL },
        },
        {
          name: "Max",
          type: "line",
          smooth: 0.25,
          data: max,
          showSymbol: false,
          lineStyle: { width: 1.5, color: ACCENT, type: "dashed" },
          itemStyle: { color: ACCENT },
        },
      ],
    };
  }, [rows, sizeVariant]);
  if (!rows.length) return <RowsPlaceholder />;
  return (
    <WidgetShell subtitle={sizeVariant === "s" ? undefined : "Min / avg / max speed per flight (km/h)"}>
      <PerfChart option={option} />
    </WidgetShell>
  );
}

export function AltitudeBandWidget({ sizeVariant }: WidgetComponentProps) {
  const rows = useFlightRows();
  const option = useMemo(() => {
    const min = valuesOf(rows, (r) => r.min_altitude_m);
    const avg = valuesOf(rows, (r) => r.avg_altitude_m);
    const max = valuesOf(rows, (r) => r.max_altitude_m);
    return {
      grid: baseGrid(sizeVariant),
      legend: {
        show: sizeVariant !== "s",
        textStyle: { color: readThemeColors().textMuted, fontSize: 11 },
        right: 12,
        top: 4,
      },
      xAxis: makeIndexXAxis(sizeVariant, rows.length),
      yAxis: { type: "value", name: "m", nameTextStyle: { color: readThemeColors().textMuted, fontSize: 10 }, ...baseAxisStyle() },
      tooltip: indexTooltip(rows, (v) => (v === null || v === undefined ? "—" : `${v.toFixed(0)} m`)),
      series: [
        {
          name: "Max",
          type: "line",
          data: max,
          symbol: "none",
          lineStyle: { width: 1, color: ACCENT },
          areaStyle: { color: "rgba(251, 191, 36, 0.08)" },
        },
        {
          name: "Avg",
          type: "line",
          data: avg,
          symbol: "none",
          lineStyle: { width: 2, color: PRIMARY },
        },
        {
          name: "Min",
          type: "line",
          data: min,
          symbol: "none",
          lineStyle: { width: 1, color: COOL },
        },
      ],
    };
  }, [rows, sizeVariant]);
  if (!rows.length) return <RowsPlaceholder />;
  return (
    <WidgetShell subtitle={sizeVariant === "s" ? undefined : "Altitude band — min / avg / max per flight"}>
      <PerfChart option={option} />
    </WidgetShell>
  );
}

export function PeakGTrendWidget({ sizeVariant }: WidgetComponentProps) {
  const rows = useFlightRows();
  const option = useMemo(() => {
    const data = valuesOf(rows, (r) => r.peak_g);
    return {
      grid: baseGrid(sizeVariant),
      xAxis: makeIndexXAxis(sizeVariant, rows.length),
      yAxis: { type: "value", name: "g", nameTextStyle: { color: readThemeColors().textMuted, fontSize: 10 }, ...baseAxisStyle() },
      tooltip: indexTooltip(rows, (v) => (v === null || v === undefined ? "—" : `${v.toFixed(2)} g`)),
      series: [
        {
          name: "Peak G",
          type: "bar",
          data,
          barMaxWidth: sizeVariant === "l" ? 24 : 18,
          itemStyle: { color: ACCENT, borderRadius: [3, 3, 0, 0] },
          emphasis: { itemStyle: { color: "#FFDD55" } },
        },
      ],
    };
  }, [rows, sizeVariant]);
  if (!rows.length) return <RowsPlaceholder />;
  return (
    <WidgetShell subtitle={sizeVariant === "s" ? undefined : "Peak G-load per flight"}>
      <PerfChart option={option} />
    </WidgetShell>
  );
}

export function BatteryDrainWidget({ sizeVariant }: WidgetComponentProps) {
  const rows = useFlightRows();
  const option = useMemo(() => {
    const data = valuesOf(rows, (r) => r.battery_spent_pct);
    return {
      grid: baseGrid(sizeVariant),
      xAxis: makeIndexXAxis(sizeVariant, rows.length),
      yAxis: {
        type: "value",
        name: "%",
        nameTextStyle: { color: readThemeColors().textMuted, fontSize: 10 },
        ...baseAxisStyle(),
      },
      tooltip: indexTooltip(rows, (v) => (v === null || v === undefined ? "—" : `${v.toFixed(1)} %`)),
      series: [
        {
          name: "Battery drain",
          type: "line",
          smooth: 0.25,
          data,
          showSymbol: sizeVariant !== "xs",
          symbol: "circle",
          symbolSize: 6,
          lineStyle: { width: 2, color: DANGER },
          itemStyle: { color: DANGER },
          areaStyle: { color: "rgba(239, 68, 68, 0.12)" },
        },
      ],
    };
  }, [rows, sizeVariant]);
  if (!rows.length) return <RowsPlaceholder />;
  return (
    <WidgetShell subtitle={sizeVariant === "s" ? undefined : "Battery drained per flight"}>
      <PerfChart option={option} />
    </WidgetShell>
  );
}

export function FlightDurationWidget({ sizeVariant }: WidgetComponentProps) {
  const rows = useFlightRows();
  const option = useMemo(() => {
    const data = valuesOf(rows, (r) => r.duration_min);
    return {
      grid: baseGrid(sizeVariant),
      xAxis: makeIndexXAxis(sizeVariant, rows.length),
      yAxis: { type: "value", name: "min", nameTextStyle: { color: readThemeColors().textMuted, fontSize: 10 }, ...baseAxisStyle() },
      tooltip: indexTooltip(rows, (v) => (v === null || v === undefined ? "—" : `${v.toFixed(1)} min`)),
      series: [
        {
          name: "Duration",
          type: "bar",
          data,
          barMaxWidth: sizeVariant === "l" ? 24 : 18,
          itemStyle: { color: COOL, borderRadius: [3, 3, 0, 0] },
          emphasis: { itemStyle: { color: "#93C5FD" } },
        },
      ],
    };
  }, [rows, sizeVariant]);
  if (!rows.length) return <RowsPlaceholder />;
  return (
    <WidgetShell subtitle={sizeVariant === "s" ? undefined : "Duration (minutes) per flight"}>
      <PerfChart option={option} />
    </WidgetShell>
  );
}

export function DistanceCumulativeWidget({ sizeVariant }: WidgetComponentProps) {
  const rows = useFlightRows();
  const option = useMemo(() => {
    let cum = 0;
    const data: number[] = [];
    for (const row of rows) {
      cum += row.distance_km ?? 0;
      data.push(Number(cum.toFixed(2)));
    }
    return {
      grid: baseGrid(sizeVariant),
      xAxis: makeIndexXAxis(sizeVariant, rows.length),
      yAxis: { type: "value", name: "km", nameTextStyle: { color: readThemeColors().textMuted, fontSize: 10 }, ...baseAxisStyle() },
      tooltip: indexTooltip(rows, (v) => (v === null || v === undefined ? "—" : `${v.toFixed(2)} km`)),
      series: [
        {
          name: "Cumulative distance",
          type: "line",
          smooth: 0.2,
          data,
          symbol: "none",
          lineStyle: { width: 2, color: GOOD },
          areaStyle: { color: "rgba(74, 222, 128, 0.12)" },
        },
      ],
    };
  }, [rows, sizeVariant]);
  if (!rows.length) return <RowsPlaceholder />;
  return (
    <WidgetShell subtitle={sizeVariant === "s" ? undefined : "Cumulative distance over the window"}>
      <PerfChart option={option} />
    </WidgetShell>
  );
}

export function ClimbDescentWidget({ sizeVariant }: WidgetComponentProps) {
  const rows = useFlightRows();
  const option = useMemo(() => {
    const climbs = valuesOf(rows, (r) => r.total_climb_m ?? 0);
    const descents = valuesOf(rows, (r) =>
      r.total_descent_m === null || r.total_descent_m === undefined ? 0 : -r.total_descent_m,
    );
    return {
      grid: baseGrid(sizeVariant),
      legend: {
        show: sizeVariant !== "s",
        textStyle: { color: readThemeColors().textMuted, fontSize: 11 },
        right: 12,
        top: 4,
      },
      xAxis: makeIndexXAxis(sizeVariant, rows.length),
      yAxis: { type: "value", name: "m", nameTextStyle: { color: readThemeColors().textMuted, fontSize: 10 }, ...baseAxisStyle() },
      tooltip: indexTooltip(rows, (v) => (v === null || v === undefined ? "—" : `${Math.abs(v).toFixed(0)} m`)),
      series: [
        {
          name: "Climb",
          type: "bar",
          stack: "alt",
          data: climbs,
          barMaxWidth: sizeVariant === "l" ? 20 : 16,
          itemStyle: { color: GOOD },
        },
        {
          name: "Descent",
          type: "bar",
          stack: "alt",
          data: descents,
          barMaxWidth: sizeVariant === "l" ? 20 : 16,
          itemStyle: { color: DANGER },
        },
      ],
    };
  }, [rows, sizeVariant]);
  if (!rows.length) return <RowsPlaceholder />;
  return (
    <WidgetShell subtitle={sizeVariant === "s" ? undefined : "Total climb vs descent per flight"}>
      <PerfChart option={option} />
    </WidgetShell>
  );
}

export function VerticalRateWidget({ sizeVariant }: WidgetComponentProps) {
  const rows = useFlightRows();
  const option = useMemo(() => {
    const maxRate = valuesOf(rows, (r) => r.max_vertical_rate_mps);
    const minRate = valuesOf(rows, (r) => r.min_vertical_rate_mps);
    return {
      grid: baseGrid(sizeVariant),
      legend: {
        show: sizeVariant !== "s",
        textStyle: { color: readThemeColors().textMuted, fontSize: 11 },
        right: 12,
        top: 4,
      },
      xAxis: makeIndexXAxis(sizeVariant, rows.length),
      yAxis: { type: "value", name: "m/s", nameTextStyle: { color: readThemeColors().textMuted, fontSize: 10 }, ...baseAxisStyle() },
      tooltip: indexTooltip(rows, (v) => (v === null || v === undefined ? "—" : `${v.toFixed(1)} m/s`)),
      series: [
        {
          name: "Max climb",
          type: "line",
          smooth: 0.25,
          data: maxRate,
          symbol: "none",
          lineStyle: { width: 2, color: GOOD },
        },
        {
          name: "Max descent",
          type: "line",
          smooth: 0.25,
          data: minRate,
          symbol: "none",
          lineStyle: { width: 2, color: DANGER },
        },
      ],
    };
  }, [rows, sizeVariant]);
  if (!rows.length) return <RowsPlaceholder />;
  return (
    <WidgetShell subtitle={sizeVariant === "s" ? undefined : "Max climb / descent rate per flight"}>
      <PerfChart option={option} />
    </WidgetShell>
  );
}

export function FlightListWidget({ sizeVariant }: WidgetComponentProps) {
  const rows = useFlightRows();
  const slice = sliceForSize(rows, sizeVariant);
  if (!slice.length) return <RowsPlaceholder />;
  return (
    <div className={`perf-flight-list perf-flight-list--${sizeVariant}`}>
      <table className="perf-flight-list__table">
        <thead>
          <tr>
            <th>Flight</th>
            <th>Smooth</th>
            <th>Avg km/h</th>
            <th>Dist</th>
            {sizeVariant !== "s" && <th>Peak G</th>}
            {sizeVariant === "l" && <th>Battery</th>}
            {sizeVariant === "l" && <th>Duration</th>}
          </tr>
        </thead>
        <tbody>
          {slice.map((r) => (
            <tr key={r.flight_id}>
              <td>
                <strong>{r.session_name}</strong>
                <small>{r.date_display}</small>
              </td>
              <td>{r.smoothness_display}</td>
              <td>{r.avg_speed_display}</td>
              <td>{r.distance_display}</td>
              {sizeVariant !== "s" && <td>{r.peak_g_display}</td>}
              {sizeVariant === "l" && <td>{r.battery_spent_display}</td>}
              {sizeVariant === "l" && <td>{r.duration_display}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
